import { ShieldCheck, MapPin, Sparkles, Play } from "lucide-react";
import { fmtNumber } from "@/lib/format";
import { usePlayer } from "@/context/PlayerContext";

export default function ArtistHero({ artist, tracks, onPromote }) {
  const p = usePlayer();
  const featured = tracks?.[0];

  return (
    <section
      data-testid={`artist-hero-${artist.id}`}
      className="relative overflow-hidden rounded-2xl border hairline bg-ink text-cream mb-6"
    >
      {/* cover */}
      <div className="absolute inset-0 opacity-50">
        <img src={artist.cover} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
      </div>

      <div className="relative grid grid-cols-12 gap-6 p-6 md:p-10">
        {/* avatar + meta */}
        <div className="col-span-12 md:col-span-8 flex items-end gap-5">
          <img src={artist.avatar} alt="" className="w-24 h-24 md:w-32 md:h-32 rounded-2xl object-cover ring-2 ring-cream/15 shrink-0" />
          <div className="min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral">
              ◊ Artista en Ovamx
            </p>
            <h1 className="font-serif text-5xl md:text-7xl tracking-tighter leading-none mt-3 flex items-center gap-3">
              {artist.name}
              {artist.verified && <ShieldCheck className="w-7 h-7 text-coral" />}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-cream/75">
              <span>{artist.handle}</span>
              {artist.location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" />
                  {artist.location}
                </span>
              )}
              <span className="font-mono text-xs text-coral">
                {fmtNumber(artist.monthly_listeners)} oyentes/mes
              </span>
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className="col-span-12 md:col-span-4 flex md:flex-col gap-3 md:items-end md:justify-end">
          {featured && (
            <button
              data-testid="artist-play-featured"
              onClick={() => p.playTrack(featured, tracks)}
              className="inline-flex items-center gap-2 bg-coral hover:bg-coral-hover text-cream px-5 py-3 rounded-full text-sm transition"
            >
              <Play className="w-4 h-4" fill="currentColor" />
              Reproducir
            </button>
          )}
          <button
            data-testid="artist-promote"
            onClick={onPromote}
            className="inline-flex items-center gap-2 border border-cream/25 text-cream px-5 py-3 rounded-full text-sm hover:bg-cream/10 transition"
          >
            <Sparkles className="w-4 h-4" />
            Promover en Horizonte
          </button>
        </div>

        {/* bio + links */}
        <div className="col-span-12 md:col-span-8 mt-2">
          <p className="text-base text-cream/85 leading-relaxed max-w-2xl">{artist.bio}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {Object.entries(artist.links || {}).filter(([, v]) => !!v).map(([k, v]) => (
              <a
                key={k}
                href="#"
                data-testid={`artist-link-${k}`}
                className="text-xs px-3 py-1.5 rounded-full bg-cream/10 hover:bg-coral hover:text-cream transition"
              >
                {k}: <span className="text-cream/85">{v}</span>
              </a>
            ))}
          </div>
        </div>

        {/* stats */}
        <div className="col-span-12 md:col-span-4 grid grid-cols-3 gap-3 md:mt-2">
          <Stat k={fmtNumber(tracks?.reduce((a, t) => a + (t.plays || 0), 0))} v="reproducciones" />
          <Stat k={`$${tracks?.reduce((a, t) => a + (t.tips_total || 0), 0).toFixed(0)}`} v="propinas" />
          <Stat k={String(tracks?.length || 0).padStart(2, "0")} v="tracks" />
        </div>
      </div>
    </section>
  );
}

function Stat({ k, v }) {
  return (
    <div className="rounded-xl border border-cream/15 bg-cream/[0.04] p-3 text-center">
      <div className="font-serif text-xl text-cream">{k}</div>
      <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.25em] text-cream/55">{v}</div>
    </div>
  );
}
